import type {
  ForceRefreshOptions,
  UsageData,
  UsageResponse,
} from './types';

/**
 * Thin, typed fetch wrapper around `/api/token-usage`.
 *
 * Why not `axios`?
 *   * Native `fetch` is bundled with every browser, ~0 KB cost.
 *   * We need exactly two calls; a wrapper keeps the type surface tight.
 *
 * Errors:
 *   * `ApiError`    — non-2xx HTTP, malformed envelope, or `code !== 0`.
 *   * `AbortError`  — passthrough for `AbortController` cancellations.
 */

const API_BASE = '/api';

/** Thrown for any non-2xx response, malformed envelope, or `code !== 0`. */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code: number | null;
  public readonly endpoint: string;

  constructor(endpoint: string, status: number, message: string, code: number | null = null) {
    super(message);
    this.name = 'ApiError';
    this.endpoint = endpoint;
    this.status = status;
    this.code = code;
  }
}

interface RequestOptions {
  readonly signal?: AbortSignal;
}

function isObject(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function assertEnvelope<T>(endpoint: string, status: number, payload: unknown): T {
  if (!isObject(payload)) {
    throw new ApiError(endpoint, status, '响应不是合法 JSON 对象');
  }
  const rawCode = payload['code'];
  const code = typeof rawCode === 'number' ? rawCode : null;
  if (code !== 0) {
    const message =
      typeof payload['message'] === 'string'
        ? payload['message']
        : `请求失败 (code=${code ?? 'unknown'})`;
    throw new ApiError(endpoint, status, message, code);
  }
  if (!('data' in payload)) {
    throw new ApiError(endpoint, status, '响应缺少 data 字段');
  }
  return payload as unknown as T;
}

async function requestJson(
  url: string,
  init: RequestInit & RequestOptions,
  endpoint: string,
): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(endpoint, 0, `网络错误：${(err as Error).message ?? '未知原因'}`);
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch (err) {
    throw new ApiError(
      endpoint,
      response.status,
      `响应不是合法 JSON：${(err as Error).message ?? '解析失败'}`,
    );
  }

  if (!response.ok) {
    // For non-2xx we still try to extract a server-side message.
    const message =
      isObject(payload) && typeof payload['message'] === 'string'
        ? payload['message']
        : `HTTP ${response.status}`;
    throw new ApiError(endpoint, response.status, message);
  }

  return payload;
}

/**
 * GET /api/token-usage — fetch the aggregated usage snapshot.
 *
 * Behavior:
 *   * Honors the optional `ids` filter via `?ids=a,b,c`.
 *   * When `opts.fresh === true`, appends `?fresh=1` to bypass the KV cache.
 *   * When `opts.ids` is omitted, all providers are returned.
 */
export async function fetchUsage(
  opts: ForceRefreshOptions = {},
  requestOptions: RequestOptions = {},
): Promise<UsageData> {
  const params = new URLSearchParams();
  if (opts.ids && opts.ids.length > 0) {
    params.set('ids', opts.ids.join(','));
  }
  if (opts.fresh) {
    params.set('fresh', '1');
  }
  const qs = params.toString();
  const url = qs ? `${API_BASE}/token-usage?${qs}` : `${API_BASE}/token-usage`;

  const payload = await requestJson(
    url,
    { method: 'GET', signal: requestOptions.signal, cache: 'no-store' },
    'GET /api/token-usage',
  );
  const envelope = assertEnvelope<UsageResponse>('GET /api/token-usage', 200, payload);
  if (!envelope.data) {
    throw new ApiError('GET /api/token-usage', 200, '响应缺少 data');
  }
  return envelope.data;
}

/**
 * POST /api/token-usage — force-refresh (always bypasses cache).
 * Returns the same shape as `fetchUsage()`.
 */
export async function refreshUsage(
  opts: ForceRefreshOptions = {},
  requestOptions: RequestOptions = {},
): Promise<UsageData> {
  const body: Readonly<Record<string, unknown>> =
    opts.ids && opts.ids.length > 0 ? { ids: [...opts.ids] } : {};
  const url = `${API_BASE}/token-usage`;

  const payload = await requestJson(
    url,
    {
      method: 'POST',
      signal: requestOptions.signal,
      body: JSON.stringify(body),
    },
    'POST /api/token-usage',
  );
  const envelope = assertEnvelope<UsageResponse>('POST /api/token-usage', 200, payload);
  if (!envelope.data) {
    throw new ApiError('POST /api/token-usage', 200, '响应缺少 data');
  }
  return envelope.data;
}
